import { motion } from 'framer-motion';
import {
  SparklesIcon,
  BookOpenIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

interface AudienceCard {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

const AUDIENCE_CARDS: AudienceCard[] = [
  {
    icon: SparklesIcon,
    title: "I'm new to recovery",
    description:
      'Track sobriety from day one. Morning check-ins build the daily habit that keeps you sober.',
  },
  {
    icon: BookOpenIcon,
    title: "I'm working the steps",
    description:
      'All 12 steps, guided prompts, and a place to do the written work your sponsor asks for.',
  },
  {
    icon: HeartIcon,
    title: "I'm a sponsor",
    description:
      "See your people's progress, assign homework, review step work, and stay connected between meetings.",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

export function WhoItsForSection() {
  return (
    <section
      id="who"
      className="bg-[#1B263B] py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Built for everyone working a program.
          </h2>
        </motion.div>

        {/* Three-card grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {AUDIENCE_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                variants={cardVariants}
                className="rounded-2xl bg-white/5 border border-white/10 p-8 flex flex-col items-start gap-4"
              >
                <div className="p-3 rounded-xl bg-teal-500/15">
                  <Icon className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {card.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
